<template>
  <v-container fluid class="pa-4 rtl">
    <!-- Header -->
    <v-card class="header-card mb-4" elevation="2">
      <div class="d-flex align-center justify-space-between flex-wrap ga-3">
        <div>
          <div class="text-h5 font-weight-bold" style="color: #4a4f55">
            التنسيق وإدارة المعلومات
          </div>
          <div class="text-body-2 text-grey-darken-1 mt-1">
            آليات التنسيق مع المديرية ونظام المعلومات الصحية
          </div>
        </div>
        <v-btn
          prepend-icon="mdi-arrow-right"
          variant="text"
          style="color: #4a4f55"
          @click="showView('pillar-governance')"
        >
          رجوع
        </v-btn>
      </div>
    </v-card>

    <!-- KPIs -->
    <v-row class="mb-4">
      <v-col cols="12" sm="6" md="3">
        <v-card class="kpi-card warning">
          <div class="kpi-value">4/9</div>
          <div class="kpi-label">تستخدم DHIS2 بانتظام</div>
        </v-card>
      </v-col>
      <v-col cols="12" sm="6" md="3">
        <v-card class="kpi-card danger">
          <div class="kpi-value">2/9</div>
          <div class="kpi-label">تشارك البيانات مع HeRAMS</div>
        </v-card>
      </v-col>
      <v-col cols="12" sm="6" md="3">
        <v-card class="kpi-card warning">
          <div class="kpi-value">5/9</div>
          <div class="kpi-label">تُقدم تقارير دورية للـ DHO</div>
        </v-card>
      </v-col>
      <v-col cols="12" sm="6" md="3">
        <v-card class="kpi-card danger">
          <div class="kpi-value">0</div>
          <div class="kpi-label">مرافق لديها HMIS متكامل</div>
        </v-card>
      </v-col>
    </v-row>

    <!-- Table -->
    <v-card class="table-card" elevation="1">
      <div class="table-header">حالة التنسيق وإدارة المعلومات</div>
      <v-table class="custom-table">
        <thead>
          <tr>
            <th>المرفق</th>
            <th>تستخدم DHIS2؟</th>
            <th>تقارير للـ DHO؟</th>
            <th>تقارير لـ HeRAMS؟</th>
            <th>سجلات ورقية؟</th>
            <th>جودة البيانات</th>
            <th>التنسيق مع الكتلة الصحية</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="item in facilities" :key="item.name">
            <td class="font-weight-bold">{{ item.name }}</td>
            <td>
              <v-chip
                :color="yesNoColor(item.dhis2)"
                size="small"
                variant="flat"
                >{{ item.dhis2 }}</v-chip
              >
            </td>
            <td>{{ item.dhoReports }}</td>
            <td>
              <v-chip
                :color="partialColor(item.herams)"
                size="small"
                variant="flat"
                >{{ item.herams }}</v-chip
              >
            </td>
            <td>
              <v-chip
                :color="paperColor(item.paperRecords)"
                size="small"
                variant="flat"
                >{{ item.paperRecords }}</v-chip
              >
            </td>
            <td>
              <v-chip
                :color="qualityColor(item.dataQuality)"
                size="small"
                variant="flat"
                >{{ item.dataQuality }}</v-chip
              >
            </td>
            <td>
              <v-chip
                :color="partialColor(item.healthCluster)"
                size="small"
                variant="flat"
                >{{ item.healthCluster }}</v-chip
              >
            </td>
          </tr>
        </tbody>
      </v-table>
    </v-card>
  </v-container>
</template>

<script setup>
const facilities = [
  {
    name: "مستشفى الصداقة التعليمي",
    dhis2: "لا",
    dhoReports: "شهرياً",
    herams: "لا",
    paperRecords: "نعم",
    dataQuality: "متوسط",
    healthCluster: "جزئي",
  },
  {
    name: "مستشفى الأمراض النفسية",
    dhis2: "لا",
    dhoReports: "نادراً",
    herams: "لا",
    paperRecords: "نعم",
    dataQuality: "ضعيف",
    healthCluster: "لا",
  },
  {
    name: "مجمع الشيخ عثمان",
    dhis2: "نعم",
    dhoReports: "شهرياً",
    herams: "جزئياً",
    paperRecords: "لا",
    dataQuality: "جيد",
    healthCluster: "نعم",
  },
  {
    name: "مركز كود العثماني",
    dhis2: "نعم",
    dhoReports: "شهرياً",
    herams: "لا",
    paperRecords: "لا",
    dataQuality: "جيد",
    healthCluster: "جزئي",
  },
  {
    name: "مركز الممدارة",
    dhis2: "نعم",
    dhoReports: "أسبوعياً",
    herams: "لا",
    paperRecords: "لا",
    dataQuality: "ممتاز",
    healthCluster: "نعم",
  },
  {
    name: "مركز المحاريق",
    dhis2: "لا",
    dhoReports: "نادراً",
    herams: "لا",
    paperRecords: "نعم",
    dataQuality: "ضعيف",
    healthCluster: "لا",
  },
  {
    name: "مركز عبد القوي",
    dhis2: "نعم",
    dhoReports: "شهرياً",
    herams: "لا",
    paperRecords: "لا",
    dataQuality: "جيد",
    healthCluster: "جزئي",
  },
  {
    name: "مركز ماري ستبوس",
    dhis2: "لا",
    dhoReports: "—",
    herams: "لا",
    paperRecords: "لا",
    dataQuality: "—",
    healthCluster: "لا",
  },
  {
    name: "جمعية حقوق الطفل",
    dhis2: "لا",
    dhoReports: "ربع سنوي",
    herams: "لا",
    paperRecords: "نعم",
    dataQuality: "متوسط",
    healthCluster: "جزئي",
  },
];

const yesNoColor = (value) => (value === "نعم" ? "green" : "red");
const partialColor = (value) => {
  if (value === "نعم") return "green";
  if (value === "جزئي" || value === "جزئياً") return "orange";
  return "red";
};
const paperColor = (value) => (value === "لا" ? "green" : "orange");
const qualityColor = (value) => {
  if (value === "ممتاز" || value === "جيد") return "green";
  if (value === "متوسط") return "orange";
  return "red";
};

const showView = (view) => console.log("Go to:", view);
</script>

<style scoped>
.rtl {
  direction: rtl;
}

.header-card {
  border-right: 5px solid #4a4f55;
  border-radius: 18px;
  padding: 20px;
}

.kpi-card {
  border-radius: 18px;
  padding: 20px;
  text-align: center;
}

.kpi-value {
  font-size: 34px;
  font-weight: 800;
}

.kpi-label {
  margin-top: 6px;
  font-size: 13px;
}

.danger {
  background: #fdecea;
  color: #c0392b;
}

.warning {
  background: #fff4e5;
  color: #d4821a;
}

.table-card {
  border-radius: 18px;
  overflow: hidden;
}

.table-header {
  padding: 18px;
  font-size: 18px;
  font-weight: 700;
  color: #3a3f47;
  border-bottom: 1px solid #eee;
}

.custom-table {
  direction: rtl;
}

:deep(th) {
  font-weight: 800 !important;
  color: #3a3f47 !important;
  white-space: nowrap;
}

:deep(td) {
  font-size: 14px;
  white-space: nowrap;
}
</style>
